import React, { useCallback, useEffect } from "react";
import { Button, Container, Form, Tab, Tabs } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useLocation, useParams } from "react-router-dom";
import { useSmartNavigateLegacy as useNavigate } from "components/SmartLink";
import "react-toastify/dist/ReactToastify.css";
import { removeTagsHTML } from "sharedKernel/handlers/text";
import useGetProgram from "./hooks/useGetProgram";

import { Header } from "./components/header";
import { availableEvaluationTypes } from "./support/availableEvaluationTypes";
import { availableQuestionTypes } from "./support/availableQuestionTypes";
import { TabHeader } from "./components/tabHeader/tabHeader";
import { TabHeaderTitle } from "./components/tabHeader/tabHeaderTitle";
import { EvaluationForm } from "./forms/EvaluationForm";
import { GeneralForm } from "./forms/GeneralForm";
import { RegistrationForm } from "./forms/RegistrationForm";
import { useProgramForm } from "./hooks/useProgramForm";
import { ProgramInputs } from "./schemas/inputs";
import { programFormResolver } from "./schemas/programFormResolver";
import { updateProvider } from "sharedKernel/api";
import { showToast } from "components/toast";
import { MobileContainerPadding, NavigationRow, TabsWrapper } from "./styled";
import { useGetProgramFiles } from "./hooks/useGetProgramFiles";
import { mapFile, normalizeFiles } from "./forms/utils/helper";

export const ProgramForm = () => {
  const [validationMode, setValidationMode] = React.useState<
    "draft" | "publish"
  >("draft");
  const validationModeRef = React.useRef<"draft" | "publish">("draft");
  const resolver = useCallback(
    (values) =>
      programFormResolver(values, {
        isDraft: validationModeRef.current === "draft",
      }),
    []
  );
  const tabKeys = ["tabGeneralConfig", "tabForm", "tabFormAva"] as const;
  const [activeKey, setActiveKey] = React.useState<string>(tabKeys[0]);
  const [filesToDelete, setFilesToDelete] = React.useState<string[]>([]);
  const navigate = useNavigate()

  const onSuccessLaunchChallenge = async (): Promise<void> => {
    showToast("success", "Evento publicado com sucesso");
    const launched = true;
  };

  const onErrorLaunchChallenge = async (): Promise<void> => {
    showToast("error", "O evento não pode ser publicado. Verifique os campos marcados de vermelho no formulário");
    const launched = false;
  };
  const { mutate: persistLaunchChallenge } = updateProvider(
    onSuccessLaunchChallenge,
    onErrorLaunchChallenge
  );

  const {
    handleSubmit,
    register,
    control,
    watch,
    getValues,
    reset,
    formState: { errors },
  } = useForm<ProgramInputs>({
    resolver,
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const params = useParams();
  const location = useLocation();
  const challengeId = params.challengeId;
  const isEdit = Boolean(challengeId && location.pathname.includes("/edit"));

  const {
    data: challengeResponse,
    isFetching: isGetting,
    isSuccess,
  } = useGetProgram(challengeId);

  const { data: filesResponse } = useGetProgramFiles(challengeId);

  const { onSubmit, onSubmitDraft } = useProgramForm(
    isEdit && challengeResponse ? challengeResponse : undefined
  );

  const files = normalizeFiles(filesResponse);

  useEffect(() => {
    if (isEdit && isSuccess && challengeResponse && filesResponse) {
      const challenge = challengeResponse.data ?? challengeResponse;

      const formValues: Partial<ProgramInputs> = {
        title: challenge?.config?.title ?? "",
        niches: challenge?.config?.niches ?? [],
        shortDescription: challenge?.style?.shortDescription ?? "",
        longDescription: challenge?.style?.longDescription ?? "",

        lauchDate: challenge?.applicationForm?.launchDate
          ? new Date(challenge.applicationForm.launchDate)
          : challenge?.config?.step?.launchDate
            ? new Date(challenge.config.step.launchDate)
            : null,

        finalSelectionDate: challenge?.applicationForm?.finalSelectionDate
          ? new Date(challenge.applicationForm.finalSelectionDate)
          : challenge?.config?.step?.finalSelectionDate
            ? new Date(challenge.config.step.finalSelectionDate)
            : null,

        limitOfIdeas: challenge?.config?.limitOfIdeas ?? 1,
        isActive: challenge?.style?.isActive ? "public" : "private",

        videos: challenge?.style?.youtubeLink
          ? [{ name: "", url: challenge.style.youtubeLink }]
          : [],

        sections: (challenge?.applicationForm?.sections ?? []).map((s: any) => ({
          name: s.name,
          questions: s.fields?.map((f: any) => ({
            name: f.name,
            description: f.description,
            isRequired: f.isRequired,
            type:
              availableQuestionTypes.find((qt) => qt.value === f.type) ||
              availableQuestionTypes[0],
            options: f.options ?? [],
          })),
        })),

        evaluations: (challenge?.evaluation?.steps ?? []).map((step: any) => ({
          name: step.name,
          startDate: step.startDate
            ? new Date(step.startDate).toISOString().split("T")[0]
            : "",
          endDate: step.closingDate
            ? new Date(step.closingDate).toISOString().split("T")[0]
            : "",
          type:
            availableEvaluationTypes.find(
              (t) => t.value === (step?.typeId ?? step?.type?.id)
            ) ?? availableEvaluationTypes[0],
          weightPercent: step.weightPercent,
          sections: step.sections?.map((sec: any) => ({
            name: sec.name,
            criteria: sec.fields,
            isDeleted: sec.isDeleted
          })),
          isDeleted: step.isDeleted
        })),

        coverImage: files
          .filter((f) => f.type === "coverImage")
          .map(mapFile),

        homePageSlider: files
          .filter((f) => f.type === "homePageSlider")
          .map(mapFile),

        presentationVideo: files
          .filter((f) => f.type === "presentationVideo")
          .map(mapFile),

        supportFiles: files
          .filter((f) => f.type === "supportFiles")
          .map(mapFile),
      };

      reset(formValues);
    }
  }, [isEdit, isSuccess, challengeResponse, filesResponse, reset]);

  const goPrev = () => {
    const currentIdx = tabKeys.indexOf(activeKey as any);
    if (currentIdx > 0) setActiveKey(tabKeys[currentIdx - 1]);
  };
  const goNext = () => {
    const currentIdx = tabKeys.indexOf(activeKey as any);
    if (currentIdx < tabKeys.length - 1) setActiveKey(tabKeys[currentIdx + 1]);
  };

  const handleDraftSubmit = async () => {
    validationModeRef.current = "draft";
    setValidationMode("draft");

    await handleSubmit(
      async (data) => {
        try {
          const id = await onSubmitDraft(data, challengeId, filesToDelete);

          showToast("success", "Rascunho salvo com sucesso");

          navigate(`/hub/company/challenge/${id}`, {
            state: { setToLaunched: false }
          });
        } catch (error) {
          showToast("error", "Programa não salvo. Revise o formulário");
        }
      },
      (errors) => {
        showToast("error", "Revise os campos obrigatórios");
      }
    )();
  };
  const launchChallenge = async (id: string): Promise<void> => {
    if (!id) {
      showToast("error", "ID inválido para publicação");
      return;
    }

    persistLaunchChallenge(
      { url: `/v1/hub/company/challenge/${id}/launch` },
      {
        onSuccess: () => {
          showToast("success", "Evento publicado com sucesso");
          navigate(`/hub/company/challenge/${id}`, {
            state: { setToLaunched: true },
          });
        },
        onError: () => {
          showToast(
            "error",
            "O evento não pode ser publicado. Verifique os campos"
          );

          navigate(`/hub/company/challenge/${id}/edit`, {
            state: { setToLaunched: true },
          });
        },
      }
    );
  };
  const handlePublishSubmit = async () => {
    validationModeRef.current = "publish";
    setValidationMode("publish");

    await handleSubmit(
      async (data) => {
        try {
          const result = await onSubmitDraft(data, challengeId, filesToDelete);

          const id =
            typeof result === "string"
              ? result
              : result?.data?.id || result?.id;

          if (!id) {
            throw new Error("Invalid challenge id returned from draft");
          }

          await launchChallenge(id);

          showToast("success", "Programa publicado com sucesso");
        } catch (error) {
          showToast("error", "Programa não publicado. Revise o formulário");
        }
      },
      (formErrors) => {
        showToast(
          "error",
          "Programa não publicado. Revise os campos obrigatórios"
        );
      }
    )();
  };

  const formValues = watch();

  const validateRequiredFields = (values: Partial<ProgramInputs>): boolean => {
    if (!values.title || values.title.trim().length < 4 || values.title.trim().length > 140) {
      return false;
    }

    if (validationMode === "draft") {
      return true;
    }

    if (!values.niches || values.niches.length === 0) {
      return false;
    }

    if (!values.shortDescription || values.shortDescription.trim().length < 4 || values.shortDescription.trim().length > 280) {
      return false;
    }

    const longDescText = values.longDescription ? removeTagsHTML(values.longDescription).trim() : "";
    if (longDescText.length < 4 || longDescText.length > 5000) {
      return false;
    }

    if (!values.sections || values.sections.length === 0) {
      return false;
    }
    for (const section of values.sections) {
      if (!section.name || section.name.trim().length === 0) {
        return false;
      }
      if (section.questions && section.questions.length > 0) {
        for (const question of section.questions) {
          if (!question.name || question.name.trim().length === 0) {
            return false;
          }
          if (!question.type) {
            return false;
          }
          if (question.isRequired === undefined) {
            return false;
          }
          if (question.isHiddenFromReviwers === undefined) {
            return false;
          }
        }
      }
    }

    if (!values.evaluations || values.evaluations.length === 0) {
      return false;
    }
    for (const evaluation of values.evaluations) {
      if (evaluation.isDeleted) {
        continue;
      }
      if (!evaluation.name || evaluation.name.trim().length === 0) {
        return false;
      }
      if (!evaluation.type) {
        return false;
      }
      const startDateStr = evaluation.startDate
        ? (typeof evaluation.startDate === 'string' ? evaluation.startDate : evaluation.startDate.toISOString().split('T')[0])
        : '';
      if (!startDateStr || startDateStr.trim().length === 0) {
        return false;
      }
      const endDateStr = evaluation.endDate
        ? (typeof evaluation.endDate === 'string' ? evaluation.endDate : evaluation.endDate.toISOString().split('T')[0])
        : '';
      if (!endDateStr || endDateStr.trim().length === 0) {
        return false;
      }

      if (evaluation.type?.value === 'SPECIAL_COMPLIANCE' && evaluation.criteria) {
        for (const criterion of evaluation.criteria) {
          if (!criterion.name || criterion.name.trim().length === 0) {
            return false;
          }
        }
      }

      if ((evaluation.type?.value === 'PERFORMANCE_0_5' || evaluation.type?.value === 'PERFORMANCE_1_5') && evaluation.sections) {
        for (const section of evaluation.sections) {
          if (!section.name || section.name.trim().length === 0) {
            return false;
          }
          if (section.criteria) {
            for (const criterion of section.criteria) {
              if (!criterion.name || criterion.name.trim().length === 0) {
                return false;
              }
            }
          }
        }
      }
    }

    if (!values.isActive) {
      return false;
    }

    if (!values.coverImage || (Array.isArray(values.coverImage) && values.coverImage.length === 0)) {
      return false;
    }

    if (!values.homePageSlider || (Array.isArray(values.homePageSlider) && values.homePageSlider.length === 0)) {
      return false;
    }

    return true;
  };

  const canPublish = Boolean(
    (!isEdit || (isEdit && challengeResponse && !challengeResponse.data?.isLaunched)) &&
    validateRequiredFields(formValues)
  );

  return (
    <Container fluid style={{ backgroundColor: "var(--background)" }}>
      <MobileContainerPadding />
      <Form className="w-100" noValidate>
        <Header
          canPublish={canPublish}
          onSaveDraft={handleDraftSubmit}
          onPublish={handlePublishSubmit}
        ></Header>
        <TabsWrapper>
        <Tabs
          activeKey={activeKey}
          onSelect={(k) => setActiveKey(k as string)}
          className="bg-transparent pt-10"
        >
          <Tab
            eventKey="tabGeneralConfig"
            title={<TabHeader itemKey="1" title="Configurações Gerais" />}
            className="bg-transparent"
          >
            <TabHeaderTitle
              title="Configurações Gerais"
              onClick={() => {}}
            />
            <GeneralForm
              errors={errors}
              register={register}
              control={control}
              watch={watch}
              filesToDelete={filesToDelete}
              setFilesToDelete={setFilesToDelete}
            />
          </Tab>
          <Tab
            eventKey="tabForm"
            title={<TabHeader itemKey="2" title="Formulário de inscrição" />}
            className="bg-transparent"
          >
            <TabHeaderTitle
              title="Formulário de incrição"
              onClick={() => {}}
            />
            <RegistrationForm
              errors={errors}
              register={register}
              control={control}
              getValues={getValues}
            />
          </Tab>
          <Tab
            eventKey="tabFormAva"
            title={<TabHeader itemKey="3" title="Formulário de avaliação" />}
            className="bg-transparent"
          >
            <TabHeaderTitle
              title="Formulário de avaliação"
              onClick={() => {}}
            />
            <EvaluationForm
              errors={errors}
              register={register}
              control={control}
              getValues={getValues}
            />
          </Tab>
        </Tabs>
        </TabsWrapper>

        <NavigationRow>
          {activeKey !== tabKeys[0] && (
            <Button
              style={{
                borderColor: "var(--primary)",
                background: "var(--white)",
                color: "var(--primary)",
              }}
              onClick={goPrev}
            >
              Voltar
            </Button>
          )}
          {activeKey !== tabKeys[tabKeys.length - 1] && (
            <Button
              style={{
                background: "var(--primary)",
                color: "var(--white)",
                marginLeft: "auto",
              }}
              onClick={goNext}
            >
              Próximo
            </Button>
          )}
        </NavigationRow>
      </Form>
    </Container>
  );
};
